import { useEffect, useRef } from "react";
import { deviceController } from "../AppController";
import { useAppSelector, useAppDispatch } from "./redux";
import {
  setAssignedDevice,
  stopDeviceOnlinePolling,
  setDevicePollingTimedOut,
  setSseDeviceOnlineReceived,
  markDeviceOnlineLocally,
} from "../redux/Clinic/clinic.slice";

const POLL_INTERVAL_MS = 5_000;
// Default cap when the caller doesn't specify one. A device that just needs to
// reconnect to Wi-Fi (no OTA) reports in well under this; a wrong password
// never does, so this is also our "the password didn't work" backstop.
const DEFAULT_MAX_DURATION_MS = 120_000; // 2 minutes

/**
 * Runs globally (mounted in Mobile layout) to detect when a newly-provisioned
 * Circadia device comes online after Wi-Fi setup.
 *
 * Starts only when Redux `deviceOnlinePolling` is non-null (set by ConnectDevice
 * after provisioning succeeds). Polls every 5 s up to a firmware-aware cap
 * (timeoutMs on the polling state, default 2 minutes).
 *
 * On is_online: true  → patches assignedDevice.device_conn_status = 1, stops.
 * On timeout          → stops and flags devicePollingTimedOut (drives the
 *                       "device didn't connect — check Wi-Fi password" banner).
 */
export const useDeviceOnlinePolling = (): void => {
  const dispatch = useAppDispatch();
  const pollingState = useAppSelector(
    (state) => state.clinic.deviceOnlinePolling
  );
  const assignedDevice = useAppSelector((state) => state.clinic.assignedDevice);

  // Use refs to avoid stale closures inside the interval
  const pollingStateRef = useRef(pollingState);
  const assignedDeviceRef = useRef(assignedDevice);
  pollingStateRef.current = pollingState;
  assignedDeviceRef.current = assignedDevice;

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Baseline of the device's cloud status captured on the FIRST poll of each
  // session. Used to reject a *stale* `is_online: true`: when reconfiguring (or
  // re-setting up a previously-seen device), the REST status can still read
  // "online" with an old `updated_at` because the cloud hasn't noticed the
  // device dropped off after the new credentials yet. Without this, the
  // immediate first poll reads that stale "online" and calls
  // stopDeviceOnlinePolling() within ~30ms — which both advances the flow
  // prematurely AND tears down the SSE stream before it ever opens (the SSE
  // hook keys off the same deviceOnlinePolling state). We only accept online
  // once the status row actually changes; comparing server timestamps to each
  // other keeps this free of phone/cloud clock skew.
  const baselineRef = useRef<{
    captured: boolean;
    wasOnline: boolean;
    updatedAt: string | null;
  }>({ captured: false, wasOnline: false, updatedAt: null });

  useEffect(() => {
    // No active polling request — clear any leftover interval and bail
    if (!pollingState) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const { deviceId, startedAt: _startedAt } = pollingState;

    // Fresh session — discard any baseline from a previous provisioning run.
    baselineRef.current = {
      captured: false,
      wasOnline: false,
      updatedAt: null,
    };

    const poll = async () => {
      const current = pollingStateRef.current;
      if (!current) return;

      // Enforce the (firmware-aware) hard cap. Factory devices pass a longer
      // timeoutMs because their one-time OTA delays the first online report.
      const maxDuration = current.timeoutMs ?? DEFAULT_MAX_DURATION_MS;
      if (Date.now() - current.startedAt >= maxDuration) {
        clearInterval(intervalRef.current!);
        intervalRef.current = null;
        dispatch(stopDeviceOnlinePolling());
        dispatch(setDevicePollingTimedOut(true));
        return;
      }

      try {
        const result = await deviceController.getDeviceConnectionStatus({
          deviceId,
        });

        if (!result.success || !result.data) return;

        const isOnline = !!result.data.is_online;
        const updatedAt = result.data.updated_at ?? null;

        // Capture the baseline on the first response, then ignore it as a
        // verdict — a device that just received Wi-Fi creds cannot legitimately
        // be online yet (it still has to reboot out of AP mode, join the
        // network, and connect to AWS IoT, which takes several seconds).
        const base = baselineRef.current;
        if (!base.captured) {
          baselineRef.current = {
            captured: true,
            wasOnline: isOnline,
            updatedAt,
          };
          return;
        }

        // A *fresh* online signal is one where the status row has changed since
        // we started waiting: the device transitioned into online, or its
        // `updated_at` advanced past the baseline. A stale "online" carried
        // over from before provisioning has the same `updated_at` and is
        // ignored until the device actually reports in.
        const statusRowChanged =
          updatedAt !== base.updatedAt || isOnline !== base.wasOnline;
        const freshlyOnline = isOnline && statusRowChanged;

        if (freshlyOnline) {
          clearInterval(intervalRef.current!);
          intervalRef.current = null;

          // Always raise the dedicated "device confirmed online" flag, even
          // when there's no assignedDevice to patch yet (the device can come
          // online before it's assigned in this flow). Without this, the line
          // below would skip the conn_status patch AND we'd still stop polling,
          // leaving the wifi-waiting-online watcher with nothing to react to.
          dispatch(setSseDeviceOnlineReceived(true));
          // Cache it as recently-online so list screens (Recent setups) can
          // overlay it onto their slower REST status.
          dispatch(markDeviceOnlineLocally({ deviceId }));

          // Patch device_conn_status to 1 (online) in Redux when we do have a
          // device on file (keeps the device card / status UI correct).
          const current_device = assignedDeviceRef.current;
          if (current_device) {
            dispatch(
              setAssignedDevice({ ...current_device, device_conn_status: 1 })
            );
          }

          dispatch(stopDeviceOnlinePolling());
        }
      } catch {
        // Network error during poll — keep going until timeout
      }
    };

    // Run once immediately, then on interval
    poll();
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // Re-run only when pollingState changes (new poll started or stopped)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollingState?.deviceId, pollingState?.startedAt]);
};
